/**
 * Long-press to grab, drag to reorder, with the page following the finger.
 *
 * Pointer events by hand, because the alternatives don't work here. HTML5
 * draggable is desktop-only — no mobile browser fires it from touch — and a
 * drag-and-drop library would be a dependency carried into the APK for one list
 * on one screen. Pointer events cover touch and mouse with the same code.
 *
 * The arithmetic lives in lib/reorder, tested. What's left here is the gesture,
 * and the gesture is all about not stealing input that meant something else:
 *
 *  - A long press, not an immediate grab, because these rows are buttons first.
 *    Tapping one selects the exercise and that has to keep working.
 *  - Moving before the press completes cancels it. Otherwise a flick to scroll
 *    the page turns into a drag, and the list fights the scroll it should have
 *    allowed.
 *  - A grab that becomes a drag suppresses the click that the release would
 *    otherwise fire, or every reorder would also select something.
 *
 * ## Coordinates are in the DOCUMENT, not the viewport
 *
 * This is the part that matters, and the first version got it wrong twice over.
 * It measured the rows in viewport coordinates once at grab time and blocked the
 * page from scrolling at all, on the reasoning that a still list keeps its
 * measurements true. Both halves were a mistake: a destination below the fold
 * could not be reached — the row travelled to the bottom of the screen and
 * stopped — and on a six-exercise queue with the rest timer pinned above it, that
 * is most of the list.
 *
 * Holding a row near an edge now scrolls the page, and every measurement is in
 * document space so scrolling cannot invalidate it. The rows genuinely do not
 * move in the document while a drag is in flight; only the window does. Viewport
 * coordinates would go stale on the first scrolled pixel.
 */

import React, { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'

import { edgeScrollVelocity, moveItem, targetIndex } from '../lib/reorder'

/** Long enough not to fire on a tap, short enough not to feel broken. */
const LONG_PRESS_MS = 400
/** Past this, the finger is scrolling the page, not settling on a row. */
const CANCEL_PX = 10

export function DragList<T>({
  items,
  idOf,
  onReorder,
  renderItem,
  handleOnly = false,
  itemStyle,
}: {
  items: T[]
  /** Row identity, for React and to decide what can be dragged. Null for a row
   *  that can't be ordered — see applyOrder on unranked items. */
  idOf: (item: T) => string | number | null | undefined
  /** The reordered list, once a drag has actually moved something. Items rather
   *  than ids: the caller already knows what its own items mean, and a plan draft
   *  identifies rows by string key while a workout queue uses exercise ids. */
  onReorder: (next: T[]) => void
  renderItem: (item: T, state: { dragging: boolean }) => ReactNode
  /**
   * Drag only from an element marked `data-drag-handle`, and start immediately
   * rather than on a long press.
   *
   * For rows built out of text inputs. A long press inside an input means "put
   * the cursor there and select a word", and stealing it to start a drag would
   * make the fields unusable — which is most of a plan editor. A handle has no
   * other job, so there is nothing to disambiguate and nothing to wait for.
   */
  handleOnly?: boolean
  /** Merged into each row, for lists whose rows are not list-shaped. */
  itemStyle?: React.CSSProperties
}) {
  const [from, setFrom] = useState<number | null>(null)
  const [to, setTo] = useState<number | null>(null)

  const rows = useRef<(HTMLLIElement | null)[]>([])
  /** Row midpoints in DOCUMENT coordinates — see the note above. */
  const midpoints = useRef<number[]>([])
  /** Where the finger went down, in the document. */
  const startDocY = useRef(0)
  /** Where it went down on the glass, for the scroll-vs-press decision. */
  const startClientY = useRef(0)
  /** The last known finger position on the glass. The scroll loop needs this
   *  between pointer events: a finger held still at an edge keeps scrolling, and
   *  fires no pointermove at all while it does. */
  const lastClientY = useRef(0)
  /** Mirrors `from` for the animation loop, which must not close over state. */
  const fromRef = useRef<number | null>(null)
  const raf = useRef<number | null>(null)
  const timer = useRef<number | null>(null)
  const swallowClick = useRef(false)

  const clearTimer = () => {
    if (timer.current !== null) {
      clearTimeout(timer.current)
      timer.current = null
    }
  }

  const stopLoop = () => {
    if (raf.current !== null) {
      cancelAnimationFrame(raf.current)
      raf.current = null
    }
  }

  /** Put the grabbed row under the finger, and work out where it would land. */
  const applyDrag = useCallback(() => {
    const i = fromRef.current
    if (i === null) return
    const docY = lastClientY.current + window.scrollY
    const el = rows.current[i]
    // Straight to the node, not through state. A pointermove can fire 120 times a
    // second and re-rendering the whole queue that often is how a drag ends up
    // lagging the finger it is meant to follow. Safe because `transform` is
    // deliberately absent from the style prop below: React never manages it, so
    // it never overwrites this.
    if (el) el.style.transform = `translateY(${docY - startDocY.current}px)`
    // State, because this one changes rarely and moves the insertion line. React
    // bails out when the slot hasn't changed.
    setTo(targetIndex(docY, midpoints.current, i))
  }, [])

  const loop = useCallback(() => {
    if (fromRef.current === null) return
    const velocity = edgeScrollVelocity(lastClientY.current, window.innerHeight)
    if (velocity !== 0) {
      const before = window.scrollY
      window.scrollBy(0, velocity)
      // Only re-apply when the page actually moved. At the top or bottom of the
      // document scrollBy is a no-op, and the row should sit still rather than
      // twitch against a limit it cannot pass.
      if (window.scrollY !== before) applyDrag()
    }
    raf.current = requestAnimationFrame(loop)
  }, [applyDrag])

  const reset = useCallback(() => {
    clearTimer()
    stopLoop()
    // Undo the imperative transform. Nothing else will: React never wrote it, so
    // React will not clear it either.
    for (const el of rows.current) if (el) el.style.transform = ''
    fromRef.current = null
    setFrom(null)
    setTo(null)
  }, [])

  // Neither a press nor a scroll loop may outlive the list.
  useEffect(() => () => { clearTimer(); stopLoop() }, [])

  /**
   * Stop the page scrolling itself under a grabbed row.
   *
   * `touch-action: none` is not enough on its own and the reason is easy to miss:
   * Chrome latches the value when the gesture *starts*, so setting it at grab
   * time — 400ms in, with the finger already down — has no effect on the movement
   * that follows. The page would pan on its own while the row tried to follow the
   * finger, and the two would fight.
   *
   * A non-passive touchmove listener still works mid-gesture, and preventing it
   * is what hands scrolling over to the loop above — which scrolls deliberately,
   * by an amount that depends on where the finger is. React's own onTouchMove is
   * registered passive, so it cannot do this; it has to be a manual listener.
   */
  useEffect(() => {
    if (from === null) return
    const block = (e: TouchEvent) => e.preventDefault()
    document.addEventListener('touchmove', block, { passive: false })
    return () => document.removeEventListener('touchmove', block)
  }, [from])

  const grab = (index: number, pointerId: number) => {
    const scrollY = window.scrollY
    midpoints.current = rows.current.map((el) => {
      if (!el) return Number.POSITIVE_INFINITY
      const r = el.getBoundingClientRect()
      return r.top + r.height / 2 + scrollY          // document space
    })
    fromRef.current = index
    setFrom(index)
    setTo(index)
    // Capture so the drag survives the finger leaving the row it started on —
    // which it does immediately, that being the point.
    try {
      rows.current[index]?.setPointerCapture(pointerId)
    } catch {
      /* capture unsupported; the drag still tracks, just less reliably */
    }
    // The only confirmation that the long press landed. Without it you hold,
    // nothing happens, and you let go before the timer fires.
    try {
      if ('vibrate' in navigator) navigator.vibrate(15)
    } catch {
      /* no haptics — the lift and shadow are the visual confirmation */
    }
    stopLoop()
    raf.current = requestAnimationFrame(loop)
  }

  /** Layout-neutral, unlike the border this replaced. A 2px border on a row
   *  reflows everything below it, which moves the very rows the drop target was
   *  measured against — the indicator would shift the answer it was reporting. */
  const line = (edge: 'top' | 'bottom') => (
    <span
      aria-hidden
      style={{
        position: 'absolute',
        left: 0,
        right: 0,
        [edge]: -1,
        height: 2,
        borderRadius: 1,
        background: 'var(--accent)',
      }}
    />
  )

  return (
    <ul
      className="plain"
      style={{ margin: 0 }}
      // A long press on Android otherwise raises the text-selection callout over
      // the row being dragged.
      onContextMenu={(e) => e.preventDefault()}
    >
      {items.map((item, i) => {
        const dragging = from === i
        const id = idOf(item)
        const draggable = id !== null && id !== undefined
        const showLine = to !== null && from !== null && to !== from && to === i

        return (
          <li
            key={id ?? `unordered-${i}`}
            ref={(el) => { rows.current[i] = el }}
            style={{
              display: 'block',
              position: 'relative',
              // NB: no `transform` here by design — see applyDrag. The row keeps
              // its space in the list, so nothing below it shifts while it
              // travels, which is also what keeps the midpoints true.
              zIndex: dragging ? 3 : undefined,
              opacity: dragging ? 0.9 : undefined,
              boxShadow: dragging ? '0 6px 16px rgb(0 0 0 / 28%)' : undefined,
              background: dragging ? 'var(--surface)' : undefined,
              borderRadius: dragging ? 'var(--radius)' : undefined,
              // Only while dragging: the rest of the time the page must scroll
              // normally under a finger that happens to start on a row.
              touchAction: dragging ? 'none' : undefined,
              userSelect: from !== null ? 'none' : undefined,
              WebkitUserSelect: from !== null ? 'none' : undefined,
              WebkitTouchCallout: 'none',
              ...itemStyle,
            }}
            onPointerDown={(e) => {
              if (!draggable) return
              if (e.pointerType === 'mouse' && e.button !== 0) return
              // In handle mode nothing outside the grip may start a drag, so a
              // press in an input stays a press in an input.
              if (handleOnly
                  && !(e.target as Element | null)?.closest?.('[data-drag-handle]')) return
              // Read now: the timeout below runs after this event is gone.
              const { clientY, pointerId } = e
              startClientY.current = clientY
              startDocY.current = clientY + window.scrollY
              lastClientY.current = clientY
              clearTimer()
              if (handleOnly) {
                // No wait: the handle exists to be unambiguous, and 400ms of
                // holding a grip that does nothing else just feels broken. Safe
                // because the handle carries touch-action: none from the start —
                // latched before the gesture, which is the only time it counts.
                grab(i, pointerId)
              } else {
                timer.current = window.setTimeout(
                  () => grab(i, pointerId), LONG_PRESS_MS,
                )
              }
            }}
            onPointerMove={(e) => {
              if (from === null) {
                // Still deciding whether this is a press or a scroll. Measured on
                // the glass, not in the document: before the grab the page can
                // still scroll, which moves document coordinates under a finger
                // that never left the same spot on the screen.
                if (Math.abs(e.clientY - startClientY.current) > CANCEL_PX) clearTimer()
                return
              }
              e.preventDefault()
              lastClientY.current = e.clientY
              applyDrag()
            }}
            onPointerUp={() => {
              clearTimer()
              if (from === null) return
              if (to !== null && to !== from) {
                // Cleared on a timer as well as by the click it swallows: after a
                // long drag the browser often fires no click at all, and a flag
                // left set would silently eat the next real tap instead.
                swallowClick.current = true
                window.setTimeout(() => { swallowClick.current = false }, 0)
                onReorder(moveItem(items, from, to))
              }
              reset()
            }}
            // The browser fires this when it decides the gesture was a scroll, or
            // when a second finger arrives. Either way the drag is over and must
            // not commit — the finger never chose a slot.
            onPointerCancel={reset}
            onClickCapture={(e) => {
              if (!swallowClick.current) return
              swallowClick.current = false
              e.preventDefault()
              e.stopPropagation()
            }}
          >
            {showLine && to < from && line('top')}
            {showLine && to > from && line('bottom')}
            {renderItem(item, { dragging })}
          </li>
        )
      })}
    </ul>
  )
}
